import React, { useState } from "react";
import styles from "./Contact.module.css";
import { apiRequest } from "../../api/apiHelper";

export const Contact = () => {
  const [form, setForm] = useState({
    fullName: "",
    companyName: "",
    companyEmail: "",
    phoneNumber: "",
    countryCode: "+91",
    message: "",
  });

  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  const countryCodes = [
    { code: "+91", country: "IN", flag: "🇮🇳", name: "India" },
    { code: "+1", country: "US", flag: "🇺🇸", name: "United States" },
    { code: "+44", country: "GB", flag: "🇬🇧", name: "United Kingdom" },
    { code: "+61", country: "AU", flag: "🇦🇺", name: "Australia" },
    { code: "+81", country: "JP", flag: "🇯🇵", name: "Japan" },
    { code: "+86", country: "CN", flag: "🇨🇳", name: "China" },
    { code: "+49", country: "DE", flag: "🇩🇪", name: "Germany" },
    { code: "+33", country: "FR", flag: "🇫🇷", name: "France" },
    { code: "+39", country: "IT", flag: "🇮🇹", name: "Italy" },
    { code: "+7", country: "RU", flag: "🇷🇺", name: "Russia" },
  ];

  const validate = () => {
    const newErrors = {};
    
    if (!form.fullName.trim()) 
      newErrors.fullName = "Full name is required.";
    
    if (!form.companyName.trim()) 
      newErrors.companyName = "Company name is required.";
    
    if (!form.companyEmail.trim()) 
      newErrors.companyEmail = "Company email is required.";
    else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.companyEmail)) 
      newErrors.companyEmail = "Invalid email address.";
    
    if (form.phoneNumber && !/^\d{7,15}$/.test(form.phoneNumber.replace(/[\s()-]/g, ""))) 
      newErrors.phoneNumber = "Invalid phone number.";
    
    if (!form.message.trim()) 
      newErrors.message = "Message is required.";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: undefined });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccess("");
    
    if (!validate()) return;

    setLoading(true);
   try {
         // Prepare payload expected by the backend
         const payload = {
           fullName: form.fullName,
           companyName: form.companyName,
           companyEmail: form.companyEmail,
           // backend expects `workEmail` field name — include it for compatibility
           workEmail: form.companyEmail,
           phoneNumber: form.countryCode + " " + form.phoneNumber,
           message: form.message,
         };
   
         await apiRequest("post", "/contact-info/contact", payload);
   
         setSuccess("Thank you for contacting us! We'll get back to you soon.");
         setForm({
           fullName: "",
           companyName: "",
           companyEmail: "",
           phoneNumber: "",
           countryCode: "+91",
           message: "",
         });
       }catch (err) {
      setSuccess("Failed to send message. Please try again later.");
    }
    setLoading(false);
  };

  return (
    <div className={styles.contactContainer}>
      <div className={styles.contentWrapper}>
        <div className={styles.leftSection}>
          <h1 className={styles.mainHeading}>Contact <span className={styles.highlight}>Us</span></h1>
          <p className={styles.subText}>
            Not sure what you need? The team at Galile.io will be happy to 
            listen to you and suggest solutions you hadn't considered
          </p>
          
          <div className={styles.contactInfo}>
            <div className={styles.infoItem}>
              <span className={styles.icon}>✉</span>
              <span className={styles.infoText}>sales@galile.io</span>
            </div>
          </div>

          <div className={styles.calendlySection}>
            <h3 className={styles.calendlyHeading}>Schedule a Meeting</h3>
            <p className={styles.calendlyText}>
              Prefer to talk directly? Book a 30-minute consultation with our team
            </p>
            <a 
              href="https://calendly.com/viraj-galile/30min?month=2026-03"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.calendlyButton}
            >
              📅 Book a Call
            </a>
          </div>
        </div>

        <div className={styles.formWrapper}>
          <form className={styles.contactForm} onSubmit={handleSubmit} noValidate>
            <div className={styles.formHeader}>
              <h2>We'd love to hear from you!</h2>
              <p>Let's get in touch</p>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label htmlFor="fullName">Full Name</label>
                <input
                  type="text"
                  id="fullName"
                  name="fullName"
                  value={form.fullName}
                  onChange={handleChange}
                  className={errors.fullName ? styles.errorInput : ""}
                  placeholder="Enter your full name"
                />
                {errors.fullName && (
                  <span className={styles.error}>{errors.fullName}</span>
                )}
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="companyName">Company</label>
                <input
                  type="text"
                  id="companyName"
                  name="companyName"
                  value={form.companyName}
                  onChange={handleChange}
                  className={errors.companyName ? styles.errorInput : ""}
                  placeholder="Enter your company name"
                />
                {errors.companyName && (
                  <span className={styles.error}>{errors.companyName}</span>
                )}
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label htmlFor="companyEmail">Email</label>
                <input
                  type="email"
                  id="companyEmail"
                  name="companyEmail"
                  value={form.companyEmail}
                  onChange={handleChange}
                  className={errors.companyEmail ? styles.errorInput : ""}
                  placeholder="olivia@untitledui.com"
                />
                {errors.companyEmail && (
                  <span className={styles.error}>{errors.companyEmail}</span>
                )}
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="phoneNumber">Phone number</label>
                <div className={styles.phoneInputWrapper}>
                  <select 
                    className={styles.countryCode}
                    name="countryCode"
                    value={form.countryCode}
                    onChange={handleChange}
                  >
                    {countryCodes.map((item) => (
                      <option key={item.code} value={item.code}>
                        {item.flag} {item.code}
                      </option>
                    ))}
                  </select>
                  <input
                    type="tel"
                    id="phoneNumber"
                    name="phoneNumber"
                    value={form.phoneNumber}
                    onChange={handleChange}
                    className={errors.phoneNumber ? styles.errorInput : ""}
                    placeholder="(555) 000-0000"
                  />
                </div>
                {errors.phoneNumber && (
                  <span className={styles.error}>{errors.phoneNumber}</span>
                )}
              </div>
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="message">Your Message</label>
              <textarea
                id="message"
                name="message"
                value={form.message}
                onChange={handleChange}
                className={errors.message ? styles.errorInput : ""}
                placeholder="Type your message here"
                rows={4}
              />
              {errors.message && (
                <span className={styles.error}>{errors.message}</span>
              )}
            </div>

            <button
              type="submit"
              className={styles.submitBtn}
              disabled={loading}
            >
              {loading ? "Sending..." : "Send Message"}
            </button>

            {success && <div className={styles.success}>{success}</div>}
          </form>
        </div>
      </div>
    </div>
  );
};